import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button, Form, Spinner } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import baseURL from "../../Services/Url";
import {
  createQuillModules,
  quillFormats,
  uploadQuillImage,
} from "../Components/quillConfig";
import BlogChartBuilder, { emptyChart } from "./BlogChartBuilder";
import BlogTableBuilder, { cleanTables, emptyTable } from "./BlogTableBuilder";

const blockId = () =>
  `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const emptyBlock = (type) => {
  const base = { id: blockId(), type };
  if (type === "text") return { ...base, html: "" };
  if (type === "image") {
    return { ...base, url: "", altText: "", caption: "" };
  }
  if (type === "chart") return { ...base, chart: emptyChart(1) };
  return { ...base, table: emptyTable(1) };
};

const hasText = (html = "") =>
  html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() !== "";

export const cleanCharts = (charts = []) =>
  charts
    .map((chart, index) => {
      const columns = Array.isArray(chart?.columns)
        ? chart.columns.map((column) => ({
            key: column.key,
            label: column.label?.trim() || column.key,
            type: column.type === "number" ? "number" : "text",
            ...(column.locked ? { locked: true } : {}),
          }))
        : [];

      return {
        ...chart,
        columns,
        title: chart?.title?.trim() || "",
        subtitle: chart?.subtitle?.trim() || "",
        source: chart?.source?.trim() || "",
        order: index + 1,
        data: (chart?.data || [])
          .filter((row) =>
            columns.some((column) => String(row[column.key] ?? "").trim())
          )
          .map((row) =>
            columns.reduce((nextRow, column) => {
              const value = row[column.key];
              nextRow[column.key] =
                column.type === "number" && value !== ""
                  ? Number(value)
                  : value ?? "";
              return nextRow;
            }, {})
          ),
      };
    })
    .filter((chart) => chart.title || chart.data.length > 0);

export const cleanContentBlocks = (blocks = []) =>
  blocks
    .map((block) => {
      if (block.type === "text") {
        return hasText(block.html) ? { ...block, html: block.html } : null;
      }
      if (block.type === "image") {
        return block.url
          ? {
              ...block,
              url: block.url.trim(),
              altText: block.altText?.trim() || "",
              caption: block.caption?.trim() || "",
            }
          : null;
      }
      if (block.type === "chart") {
        const chart = cleanCharts([block.chart])[0];
        return chart ? { ...block, chart } : null;
      }
      if (block.type === "table") {
        const table = cleanTables([block.table])[0];
        return table ? { ...block, table } : null;
      }
      return null;
    })
    .filter(Boolean)
    .map((block, index) => ({ ...block, order: index + 1 }));

const escapeHtmlAttribute = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export const blocksFromBlog = (blog = {}) => {
  if (Array.isArray(blog.contentBlocks) && blog.contentBlocks.length > 0) {
    return blog.contentBlocks.map((block, index) => ({
      ...block,
      id: block.id || blockId(),
      order: index + 1,
    }));
  }

  const blocks = [];
  const html = blog.content || blog.description || "";
  if (hasText(html)) blocks.push({ ...emptyBlock("text"), html });

  const charts = [
    ...(Array.isArray(blog.charts) ? blog.charts : []),
    ...(Array.isArray(blog.graphs) ? blog.graphs : []),
  ];
  charts
    .filter((chart) => chart?.type !== "table")
    .forEach((chart) => blocks.push({ ...emptyBlock("chart"), chart }));

  const legacyChartTables = charts
    .filter((chart) => chart?.type === "table")
    .map((chart) => {
      const table = {
        ...chart,
        rows: Array.isArray(chart.data) ? chart.data : [],
      };
      delete table.data;
      delete table.type;
      return table;
    });
  const tables = [
    ...(Array.isArray(blog.tables) ? blog.tables : []),
    ...legacyChartTables,
  ];
  tables.forEach((table) => blocks.push({ ...emptyBlock("table"), table }));

  return blocks.length > 0 ? blocks : [emptyBlock("text")];
};

export const legacyFieldsFromBlocks = (blocks = []) => {
  const cleaned = cleanContentBlocks(blocks);
  return {
    content: cleaned
      .filter((block) => block.type === "text" || block.type === "image")
      .map((block) => {
        if (block.type === "text") return block.html;
        const caption = block.caption
          ? `<figcaption>${escapeHtmlAttribute(block.caption)}</figcaption>`
          : "";
        return `<figure><img src="${escapeHtmlAttribute(block.url)}" alt="${escapeHtmlAttribute(
          block.altText
        )}">${caption}</figure>`;
      })
      .join("\n"),
    charts: cleaned
      .filter((block) => block.type === "chart")
      .map((block, index) => ({ ...block.chart, order: index + 1 })),
    tables: cleaned
      .filter((block) => block.type === "table")
      .map((block, index) => ({ ...block.table, order: index + 1 })),
  };
};

const AddBlockControls = ({ onAdd }) => (
  <div className="d-flex align-items-center justify-content-center gap-2 my-3">
    <span className="small text-muted">Insert:</span>
    {[
      ["text", "Text"],
      ["image", "Image"],
      ["chart", "Chart"],
      ["table", "Table"],
    ].map(([type, label]) => (
      <Button
        key={type}
        type="button"
        size="sm"
        variant="outline-primary"
        onClick={() => onAdd(type)}
      >
        + {label}
      </Button>
    ))}
  </div>
);

const BlogContentBlocks = ({ blocks = [], onChange, onError }) => {
  const [uploadingBlockId, setUploadingBlockId] = useState("");
  const blocksRef = useRef(blocks);
  const onErrorRef = useRef(onError);
  blocksRef.current = blocks;
  onErrorRef.current = onError;
  const quillModules = useMemo(
    () =>
      createQuillModules(function imageHandler() {
        uploadQuillImage(this.quill, {
          folder: "blogs/content",
          entityType: "blog-content",
          onError: (error) => onErrorRef.current?.(error),
        });
      }),
    []
  );

  const updateBlocks = (nextBlocks) =>
    onChange(nextBlocks.map((block, index) => ({ ...block, order: index + 1 })));

  const insertBlock = (index, type) => {
    const nextBlocks = [...blocks];
    nextBlocks.splice(index, 0, emptyBlock(type));
    updateBlocks(nextBlocks);
  };

  const updateBlock = (index, value) =>
    updateBlocks(
      blocksRef.current.map((block, blockIndex) =>
        blockIndex === index ? { ...block, ...value } : block
      )
    );

  const updateBlockById = (id, value) =>
    updateBlocks(
      blocksRef.current.map((block) =>
        block.id === id ? { ...block, ...value } : block
      )
    );

  const moveBlock = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= blocks.length) return;
    const nextBlocks = [...blocks];
    [nextBlocks[index], nextBlocks[target]] = [nextBlocks[target], nextBlocks[index]];
    updateBlocks(nextBlocks);
  };

  const uploadImage = async (index, file) => {
    if (!file) return;
    const block = blocks[index];
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "blogs/content");
    formData.append("entityType", "blog-content");
    setUploadingBlockId(block.id);
    try {
      const response = await axios.post(`${baseURL}/media/upload`, formData);
      const url = response.data?.media?.url || response.data?.upload?.Location;
      if (!url) throw new Error("Image upload did not return a URL");
      updateBlockById(block.id, { url });
    } catch (error) {
      if (onError) onError(error);
    } finally {
      setUploadingBlockId("");
    }
  };

  return (
    <div className="border-top pt-3 mt-3 mb-4">
      <div className="mb-2">
        <h5 className="mb-1">Content</h5>
        <p className="text-muted small mb-0">
          Add and arrange text, images, charts, and tables in the exact order they should appear.
        </p>
      </div>

      <AddBlockControls onAdd={(type) => insertBlock(0, type)} />

      {blocks.map((block, index) => (
        <React.Fragment key={block.id}>
          <div className="border rounded bg-light p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <strong className="text-capitalize">{index + 1}. {block.type}</strong>
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline-secondary"
                  disabled={index === 0}
                  onClick={() => moveBlock(index, -1)}
                >
                  Move up
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-secondary"
                  disabled={index === blocks.length - 1}
                  onClick={() => moveBlock(index, 1)}
                >
                  Move down
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-danger"
                  onClick={() => updateBlocks(blocks.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              </div>
            </div>

            {block.type === "text" && (
              <ReactQuill
                theme="snow"
                value={block.html || ""}
                onChange={(html) => updateBlock(index, { html })}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Enter content"
              />
            )}

            {block.type === "image" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Image</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    disabled={uploadingBlockId === block.id}
                    onChange={(event) => uploadImage(index, event.target.files?.[0])}
                  />
                  {uploadingBlockId === block.id && (
                    <div className="small text-muted mt-2">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Uploading image...
                    </div>
                  )}
                </Form.Group>
                {block.url && (
                  <img
                    src={block.url}
                    alt={block.altText || "Content preview"}
                    className="img-fluid rounded mb-3"
                    style={{ maxHeight: 260 }}
                  />
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Alt text</Form.Label>
                  <Form.Control
                    value={block.altText || ""}
                    onChange={(event) => updateBlock(index, { altText: event.target.value })}
                    placeholder="Describe the image for accessibility"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Caption</Form.Label>
                  <Form.Control
                    value={block.caption || ""}
                    onChange={(event) => updateBlock(index, { caption: event.target.value })}
                    placeholder="Optional image caption"
                  />
                </Form.Group>
              </>
            )}

            {block.type === "chart" && (
              <BlogChartBuilder
                compact
                charts={[block.chart]}
                onChange={(charts) => updateBlock(index, { chart: charts[0] })}
              />
            )}

            {block.type === "table" && (
              <BlogTableBuilder
                compact
                tables={[block.table]}
                onChange={(tables) => updateBlock(index, { table: tables[0] })}
              />
            )}
          </div>
          <AddBlockControls onAdd={(type) => insertBlock(index + 1, type)} />
        </React.Fragment>
      ))}
    </div>
  );
};

export default BlogContentBlocks;
